import React, { useCallback, useEffect, useRef, useState } from 'react'
import { FaPaperclip, FaTimes, FaTrash, FaPen } from 'react-icons/fa'
import { Box, Stack, Text, Button, Icon, Loading, EmptyBox, ImageViewer, FileChooser, UserChip, ConfirmButton, MentionInput, MentionText } from '@/components/lib'
import utils, { ALLOWED_MESSAGE_FILE_TYPES } from '@/utils/utils'
import api from '@/api'

// Mirrors MAX_COMMENT_ATTACHMENTS on the API, which refuses a comment carrying
// more. Enforced here too so the composer never offers to attach a file that
// would be rejected on send.
const MAX_ATTACHMENTS = 10

/**
 * The conversation on a query.
 *
 * Who can take part is decided by the API, not here: anyone who can open the
 * query can read the thread, and anyone who can act on it — the queries
 * permissions, or being one of its assignees — can add to it. `canWrite` is
 * only whether to show the composer; the server is the authority.
 *
 * Mentioning someone notifies them, and so do new comments for the people the
 * query is assigned to, so the composer offers the trial's users and nobody
 * else.
 */
export default function QueryComments ({ query, trial, users, currentUser, canWrite, onCountChange, reloadKey }) {
  const [comments, setComments] = useState([])
  const [loading, setLoading] = useState(true)
  const [posting, setPosting] = useState(false)
  const [text, setText] = useState('')
  const [attachments, setAttachments] = useState([])
  const [editingId, setEditingId] = useState(null)
  const [editingText, setEditingText] = useState('')

  const queryId = query?._id
  const canManage = utils.canManageTrial(currentUser, trial)

  // Held in a ref, not a dependency: reporting the count updates the row behind
  // this modal, which hands back a new callback, which would otherwise reload
  // the thread and report the count again, for ever.
  const countRef = useRef(onCountChange)
  countRef.current = onCountChange

  const load = useCallback(() => {
    if (!queryId) return
    // The server caps page_size at 100; a query thread that long is unheard of,
    // and asking for it keeps this to a single request.
    api.queries.getComments(queryId, 1, 100, d => {
      setComments(d.comments || [])
      setLoading(false)
      // Only when this is the whole thread. Past a hundred comments what came
      // back is a page, and reporting its length would overwrite the row's
      // true count with a smaller one.
      if (!d.nextPageAvailable) countRef.current?.(d.comments?.length || 0)
    }, err => {
      setLoading(false)
      utils.toast('error', 'Unable to load comments', err.message)
    })
    // `reloadKey` changes when something outside this component writes to the
    // thread — resolving the query leaves a comment saying who did it.
  }, [queryId, reloadKey])

  useEffect(() => { load() }, [load])

  function post () {
    // Ctrl/Cmd+Enter has no disabled state to stop a second press, and posting
    // twice would send two rounds of notifications for one comment.
    if (posting) return
    if (!text.trim() && !attachments.length) return
    setPosting(true)
    const payload = { comment: text }
    if (attachments.length) payload.attachments = attachments.map(a => a.storedName)
    api.queries.createComment(queryId, payload, comment => {
      setPosting(false)
      setText('')
      setAttachments([])
      setComments(prev => [...prev, comment])
      // Reported outside the updater: an updater has to be pure, and React may
      // run it twice, or during render — where asking the parent to re-render
      // is an error rather than just a stale count.
      countRef.current?.(comments.length + 1)
    }, err => {
      setPosting(false)
      utils.toast('error', 'Unable to post comment', err.message)
    })
  }

  function saveEdit (comment) {
    api.queries.updateComment(queryId, comment._id, { comment: editingText }, updated => {
      setComments(prev => prev.map(c => c._id === updated._id ? updated : c))
      setEditingId(null)
    }, err => utils.toast('error', 'Unable to save comment', err.message))
  }

  function remove (comment) {
    api.queries.deleteComment(queryId, comment._id, () => {
      setComments(prev => prev.filter(c => c._id !== comment._id))
      countRef.current?.(comments.length - 1)
    }, err => utils.toast('error', 'Unable to delete comment', err.message))
  }

  // Oldest first: a query thread reads as a conversation, not a feed.
  const ordered = [...comments].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))

  return (
    <Stack gap={3} flex={1} minH={0}>
      <Stack gap={3} flex={1} overflowY='auto' minH={0} maxH='420px'>
        {loading && <Loading />}
        {!loading && !ordered.length &&
          <EmptyBox
            icon='messages'
            title='No comments yet'
            description={canWrite
              ? 'Answer the query, or ask someone to, by mentioning them with @.'
              : 'Comments on this query will appear here.'}
          />}
        {ordered.map(comment => {
          // Written by the system to record a change to the query rather than
          // by a person, so it reads as a line of history rather than as
          // something said — and it can't be edited or deleted.
          if (comment.system) {
            return (
              <Stack key={comment._id} direction='row' justifyContent='center' alignItems='center' gap={2} py={1}>
                <MentionText text={comment.comment} currentUserId={currentUser?._id} fontSize='xs' color='fg.muted' />
                <Text fontSize='xs' color='fg.subtle'>{utils.formatDateTime(comment.createdAt)}</Text>
              </Stack>
            )
          }
          const isOwn = String(comment.author) === String(currentUser?._id)
          return (
            <Box key={comment._id} p={3} bg='bg.surface' borderWidth='1px' borderColor='border.default' rounded='md'>
              <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
                <UserChip compact user={comment.authorUser} />
                <Stack direction='row' gap={1} alignItems='center'>
                  <Text fontSize='xs' color='fg.muted'>
                    {utils.formatDateTime(comment.createdAt)}{comment.updatedAt ? ' (edited)' : ''}
                  </Text>
                  {isOwn &&
                    <Button
                      size='xs' variant='ghost'
                      onClick={() => { setEditingId(comment._id); setEditingText(comment.comment || '') }}
                    >
                      <Icon as={FaPen} />
                    </Button>}
                  {(isOwn || canManage) &&
                    <ConfirmButton
                      header='Really delete this comment?'
                      trigger={<Button size='xs' variant='ghost' colorPalette='red'><Icon as={FaTrash} /></Button>}
                      onConfirm={() => remove(comment)}
                      actionColor='red'
                    />}
                </Stack>
              </Stack>

              {editingId === comment._id
                ? (
                  <Stack gap={2}>
                    <MentionInput users={users} value={editingText} onChange={setEditingText} rows={3} />
                    <Stack direction='row' justifyContent='end' gap={2}>
                      <Button size='xs' variant='ghost' onClick={() => setEditingId(null)}>Cancel</Button>
                      <Button size='xs' colorPalette='primary' onClick={() => saveEdit(comment)}>Save</Button>
                    </Stack>
                  </Stack>)
                : (
                  <MentionText text={comment.comment} currentUserId={currentUser?._id} fontSize='sm' />)}

              {comment.fileUrls?.length > 0 &&
                <Stack direction='row' gap={2} flexWrap='wrap' mt={2}>
                  {comment.fileUrls.map(f => f.contentType?.startsWith('image/')
                    ? <ImageViewer key={f.url} url={f.url} withPreview />
                    : (
                      <Button
                        key={f.url} size='xs' variant='outline'
                        onClick={() => window.open(f.url, '_blank', 'noopener,noreferrer')}
                      >
                        <Icon as={FaPaperclip} mr={1} />{f.name}
                      </Button>))}
                </Stack>}
            </Box>
          )
        })}
      </Stack>

      {canWrite &&
        <Box flexShrink={0}>
          <MentionInput
            users={users}
            value={text}
            onChange={setText}
            onSubmit={post}
            rows={3}
            placeholder='Write a comment. Type @ to mention someone…'
          />
          {attachments.length > 0 &&
            <Stack direction='row' flexWrap='wrap' gap={2} mt={2}>
              {attachments.map((a, i) => (
                <Box key={i} display='flex' alignItems='center' gap={1} px={2} py={1} bg='bg.subtle' rounded='md'>
                  <Icon as={FaPaperclip} />
                  <Text fontSize='xs'>{a.name}</Text>
                  <Icon as={FaTimes} cursor='pointer' onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))} />
                </Box>
              ))}
            </Stack>}
          <Stack direction='row' justifyContent='end' alignItems='center' gap={2} mt={2}>
            {attachments.length >= MAX_ATTACHMENTS
              ? (
                <Text fontSize='xs' color='fg.muted'>
                  {MAX_ATTACHMENTS} attachments is the most one comment can carry.
                </Text>)
              : (
                <FileChooser
                  // Uploaded against the query, not the trial: the API rebuilds
                  // the key from the query's own prefix, so this has to match.
                  forType='query'
                  forObject={query}
                  accept={ALLOWED_MESSAGE_FILE_TYPES}
                  multiple
                  onComplete={file => setAttachments(prev => (
                    prev.length >= MAX_ATTACHMENTS
                      ? prev
                      : [...prev, { storedName: file.storedName, name: file.name }]
                  ))}
                  onError={err => utils.toast('error', `Upload failed: ${err}`)}
                  trigger={<Button size='xs' variant='ghost'><Icon as={FaPaperclip} mr={1} /> Attach</Button>}
                />)}
            <Button size='sm' colorPalette='primary' onClick={post} loading={posting}>Comment</Button>
          </Stack>
        </Box>}
    </Stack>
  )
}
